import type {RefObject} from 'react';
import type {Map as MapboxMap} from 'mapbox-gl';
import type {MyList} from 'server/apiHandlers/getMyListsHandler';
import useMapSelectedPlace from './useMapSelectedPlace';
import {share2Line} from 'src/utils/icons';
import BaseButton from '../BaseButton';
import ShareSheet from '../ShareSheet';
import Dropdown from '../Dropdown';
import UserTag from '../UserTag';
import MapView from './MapView';

type Props = {
  listName: string;
  listUser?: User;
  places: Partial<Place>[];
  reviews: Review[];
  myLists: MyList[];
  mapContainer: RefObject<HTMLDivElement | null>;
  children: React.ReactNode;
  headerComponent?: React.ReactNode;
  session: IronSession | null;
  map: MapboxMap | null;
};

const MapLayout = ({
  places,
  reviews,
  myLists,
  mapContainer,
  listUser,
  listName,
  children,
  headerComponent,
  session,
  map
}: Props) => {
  const {place: selectedPlace} = useMapSelectedPlace({places, map});

  return (
    <>
      <MapView
        map={map}
        places={places}
        reviews={reviews}
        myLists={myLists}
        mapContainer={mapContainer}
        className={[
          'max-[800px]:w-[calc(100%-16px)]',
          selectedPlace
            ? 'max-[800px]:grow max-[800px]:h-[calc(100dvh-240px)]'
            : 'max-[800px]:grow-0 max-[800px]:h-[calc(50vw+20px)]'
        ]}
        wrapperClassName={selectedPlace && 'max-[800px]:h-0 max-[800px]:grow'}
        session={session}
      />

      <div className="relative w-[420px] max-[1100px]:w-[360px] max-[800px]:w-full flex flex-col items-start gap-5 max-[800px]:gap-3">
        <div className="flex flex-col gap-5 max-[800px]:gap-3 pr-6 max-[800px]:pr-4 w-full">
          <h1 className="text-2xl py-1 max-[800px]:py-0 mt-2 max-[800px]:mt-0">{listName}</h1>

          <div className="flex gap-1">
            {listUser ? (
              <UserTag
                user={listUser}
                avatarSize={28}
                className="bg-(--green-secondary) text-(--green-primary) hover:bg-(--green-secondary-hover) data-[pressed]:bg-(--green-secondary-hover)"
              />
            ) : null}

            {headerComponent}

            <Dropdown items={[{component: <ShareSheet key={0} />}]}>
              <BaseButton size="small" buttonVariant="text" aria-label="Share" icon={share2Line} label="Share" />
            </Dropdown>
          </div>
        </div>

        {children}
      </div>
    </>
  );
};

export default MapLayout;
